import TimezoneProvider from "lib/TimezoneProvider";
import { GET_INVOICES_QUERY } from "./queries";
import InvoicePage from "./InvoicePage";
import { QueryParamProvider } from "use-query-params";
import { ReactRouter6Adapter } from "use-query-params/adapters/react-router-6";
import { buildClientRecord, buildClientRoleRecord, buildClientCampaignRecord, buildContractRecord, buildProgramGroupRecord, buildVendorRecord } from "lib/storybook/utils";
import { expect, userEvent, screen, within, waitFor } from "@storybook/test";


export default {
  title: "pages/Invoice",
  component: InvoicePage,
};

const Template = (args) => {

  return (
    <QueryParamProvider adapter={ReactRouter6Adapter}>
      <TimezoneProvider>
        <InvoicePage {...args} />
      </TimezoneProvider>
    </QueryParamProvider>
  );
};

const INVOICE_LINE_ITEMS = [
  {
    "id": "1",
    "position": 1,
    "clientCampaignName": "A Client Campaign",
    "clientCampaignExportableId": "CAMP-933b2e0",
    "subtotalInCents": 2053000,
    "billableLeadCount": 203,
    "__typename": "InvoiceLineItem"
  },
  {
    "id": "2",
    "position": 2,
    "clientCampaignName": "A different one",
    "clientCampaignExportableId": "CAMP-222b2e0",
    "subtotalInCents": 213550,
    "billableLeadCount": 137,
    "__typename": "InvoiceLineItem"
  },
  {
    "id": "3",
    "position": 3,
    "clientCampaignName": "Called this something else",
    "clientCampaignExportableId": "CAMP-4552e0",
    "subtotalInCents": 23550,
    "billableLeadCount": 43,
    "__typename": "InvoiceLineItem"
  },
  {
    "id": "4",
    "position": 4,
    "clientCampaignName": "Another other",
    "clientCampaignExportableId": "CAMP-111b2e0",
    "subtotalInCents": 34500,
    "billableLeadCount": 117,
    "__typename": "InvoiceLineItem"
  },
  {
    "id": "5",
    "position": 5,
    "clientCampaignName": "Baked brown bread",
    "clientCampaignExportableId": "CAMP-5772e0",
    "subtotalInCents": 793400,
    "billableLeadCount": 52,
    "__typename": "InvoiceLineItem"
  },
]

function buildInvoice(id, month, year, contract, client, vendor, lineItemIndex, numLineItems) {
  return {
    id: id,
    month: month,
    year: year,
    sentAt: '2021-01-01T19:57:00Z',
    contract: {
      "id": contract.id,
      "client": {
        "id": client.id,
        "name": client.attributes.name,
        "__typename": "Client"
      },
      "vendor": {
        "id": vendor.id,
        "name": vendor.attributes.name,
        "__typename": "Vendor"
      },
      "__typename": "Contract"
    },
    "invoiceLineItems": INVOICE_LINE_ITEMS.slice(lineItemIndex, numLineItems),
    "__typename": "Invoice"
  }
}

const cc1Id = '1'
const cc2Id = '2'
const client = buildClientRecord('1', 'Vega University', { "client-campaigns": { data: [{ type: 'client-campaigns', id: cc1Id }, { type: 'client-campaigns', id: cc2Id }] } })
const vendor = buildVendorRecord('1', 'Education Station')
const contract = buildContractRecord(client.id, vendor.id)
const programGroup = buildProgramGroupRecord('1', 'Health', client.id)
const clientCampaign1 = buildClientCampaignRecord(cc1Id, 'Fancy Campaign Name', contract.id, programGroup.id, {"public-id": '933b2e0', "billable-lead-count": 50, "total-cost-in-cents": 156104,})
const clientCampaign2 = buildClientCampaignRecord(cc2Id, "Je m'appelle Demure", contract.id, programGroup.id, {"public-id": '2cc65fb', "billable-lead-count": 73, "total-cost-in-cents": 293102,})
const clientRole = buildClientRoleRecord('1', client.id, { permitEdit: true })

export const Default = {
  render: Template,
  args: {
    clientId: "1",
  },
  parameters: {
    reactRouter: {
      routePath: "/clients/:clientId/invoices",
      routeParams: { clientId: "1" },
      browserPath: "/clients/1/invoices",
    },
    orbit: {
      user: {
      },
      records: [
        client,
        vendor,
        contract,
        programGroup,
        clientCampaign1,
        clientCampaign2,
        clientRole
      ],
    },
    providers: {
      apollo: {
        mocks: [
          // Seems to be an issue where the invoice page is calling the GET_INVOICE_QUERY twice.
          // This first empty one is needed so the query doesn't become exhausted
          {
            request: {
              query: GET_INVOICES_QUERY,
              variables: {clientId: '1'},
            },
            result: {
              "data": {
                "invoices": []
              }
            },
          },
          {
            request: {
              query: GET_INVOICES_QUERY,
              variables: {clientId: '1'},
            },
            result: {
              "data": {
                "invoices": [
                  buildInvoice(2, 11, 2020, contract, client, vendor, 2, 5),
                  buildInvoice(1, 10, 2020, contract, client, vendor, 0, 2),
                ]
              }
            },
          },
        ],
      },
    },
  },
}
export const WithPermitEdit = {
  ...Default,
};

export const WithoutPermitEdit = {
  ...Default,
  parameters: {
    ...Default.parameters,
    orbit: {
      ...Default.parameters.orbit,
      records: [
        ...Default.parameters.orbit.records.filter((r) => r.type !== 'client-roles'),
        buildClientRoleRecord('1', client.id, { permitEdit: false }),
      ],
    },
  },
};

export const SendOrLockModal = {
  ...Default,
  play: async ({ canvasElement }) => {
    const canvas = within(canvasElement);

    await userEvent.click(
      await canvas.findByRole('button', { name: /Lock or Send All Draft Invoices/i })
    );

    await waitFor(() => {
      expect(screen.getByRole('button', { name: /Lock Billable Status/i })).toBeInTheDocument();
      expect(screen.getByRole('button', { name: /Lock & Send Invoice/i })).toBeInTheDocument();
    });
  },
};

export const ResendModal = {
  ...Default,
  play: async ({ canvasElement }) => {
    const canvas = within(canvasElement);

    const novRow = await canvas.findByText('November 2020');
    await userEvent.click(novRow)

    const vendorText = await canvas.findByText('$40.16');
    await userEvent.click(vendorText);

    await expect(screen.getByRole('button', { name: /Resend/i })).toBeInTheDocument();
  }
};
